import { useState } from 'react';
import SubmissionFlowFooter from '@common/components/SubmissionFlowFooter/SubmissionFlowFooter';
import SubmissionFlowHeader from '@common/components/SubmissionFlowHeader/SubmissionFlowHeader';
import Tabs from '@common/components/Tabs/Tabs';
import { documentTypeToCustomLabelMap } from '@common/constants';
import { IDocumentDto } from '@common/features/submission/types';
import { useGetAlternativeTextFile } from '@common/hooks/useGetAlternativeTextFile';
import { DocumentType, DocumentStatus } from '@common/types';
import { HtmlDocument } from '@components/HtmlDocument/HtmlDocument';
import { ReadonlyDocumentContent } from '../../components/DocumentContentDetails/ReadonlyDocumentContent';
import { DocumentProcessingDetails } from '../../components/DocumentContentDetails/types';
import { DocumentVersionHistory } from '../../components/DocumentVersionHistory/DocumentVersionHistory';
import { getDocumentAttributes, getEmcShortLinkDetails } from '../../helpers';
import * as Styled from './styles';
import { useFetchDocumentHtmlContent } from '@common/hooks/useFetchDocumentHtmlContent';
import { formatDocumentSubTitle } from '@common/utils/utils';
import { selectConfiguration } from '@common/features/configuration/selectors';
import { useAppSelector } from '@common/hooks/redux';
import { useFeatureFlags } from '@common/hooks/useFeatureFlags';
import { useFetchCompanyConfiguration } from '@common/features/configuration/useFetchCompanyConfiguration';
import { Button, ButtonTypes } from '@common/components';
import theme from '@common/theme';

export interface IReadonlyDocumentDetailsContent {
  showHistoryFiltering: boolean;
  companyId: number;
  contentType: DocumentType;
  title: string;
  document: IDocumentDto;
  linkedProductId?: number | undefined;
  onCancel: () => void;
  onFileDownload?: () => void;
  onSecondaryFileDownload?: () => void;
  displayHtmlViewTab?: boolean;
  submissionId: string;
  isAdmin: boolean;
  printUrl?: string;
  secondaryOriginalFileName: string | undefined;
  documentProcessingDetails: DocumentProcessingDetails;
}

const ReadonlyDocumentDetailsContent = ({
  showHistoryFiltering,
  companyId,
  contentType,
  title,
  document,
  linkedProductId,
  onCancel,
  onFileDownload,
  onSecondaryFileDownload,
  displayHtmlViewTab,
  submissionId,
  isAdmin,
  printUrl,
  secondaryOriginalFileName,
  documentProcessingDetails,
}: IReadonlyDocumentDetailsContent) => {
  const documentTitle = document?.documentTitle;
  const fileName = document?.originalFileName ?? document?.link;
  const version = document?.version ?? 1;
  const approvalDate = document?.regulatorApprovalDate;
  const approved = !!(document?.regulatorApprovalDate ?? document?.approvedDate);
  const firstAuthorisationDate = document?.authorisedDate;
  const regulator = document?.approvedByRegulator;
  const reasonsForSubmission = document?.reasonsForSubmission;
  const documentState = document?.state;
  const hasDigitalVersion = document?.hasDigitalVersion ?? null;
  const url = document?.link;
  const details = document?.details ?? '';

  const attributes = getDocumentAttributes(contentType, isAdmin, documentProcessingDetails.documentProcessingFlow);

  const { showPrintButton } = useFeatureFlags();
  
  const onPrint = () => {
    window.open(
      (printUrl ?? '').replace(':documentId', document!.documentId.toString())
        .replace(':documentType', document!.type.toString())
        .replace(':version', document!.version.toString()),
      '_blank'
    );
  };

  const [selectedTab, setSelectedTab] = useState<string>(`${documentTypeToCustomLabelMap[contentType]} details`);

  const { data: htmlContent, documentFetching } = useFetchDocumentHtmlContent(
    document && attributes.hasHtmlContent
      ? {
          documentId: document?.documentId,
          version: document?.version,
          type: contentType,
          submissionId: document.status === DocumentStatus.Published ? null :  Number(submissionId),
        }
      : undefined,
  );

  const { alternativeTextFileName, openAlternativeTextFile } = useGetAlternativeTextFile(
    submissionId,
    document.documentId.toString(),
    attributes,
  );

  const reasonForRetirement =
    document?.reasonForRetirement?.length! > 0
      ? { label: document?.reasonForRetirement as string, value: document?.reasonForRetirement as string }
      : undefined;

  // The Print button is shown only if:
  // - the feature flag is enabled, and
  // - the user is an admin, or
  // - the document is not an SMPC, or
  // - the document has TVT files, and
  // - the selected tab is 'View html'
  const canPrint = showPrintButton && (
    isAdmin
    || (!!document ? document?.type !== DocumentType.Smpc : false)
    || Boolean(document?.hasTvtFiles)
  ) && selectedTab === 'View html';

  const getTabs = () => {
    const items = [
      {
        label: `${documentTypeToCustomLabelMap[contentType]} details`,
        content: (
          <ReadonlyDocumentContent
            documentTitle={documentTitle ?? ''}
            version={version ?? 1}
            fileName={fileName ?? ''}
            contentType={contentType}
            approvalDate={approvalDate ?? null}
            approved={approved}
            firstAuthorisationDate={firstAuthorisationDate ?? null}
            regulator={regulator ?? ''}
            reasonsForSubmission={reasonsForSubmission ?? []}
            reasonForRetirement={reasonForRetirement}
            url={url}
            documentState={documentState!}
            hasDigitalVersion={hasDigitalVersion}
            onFileClick={onFileDownload}
            contentDescription={document.contentDescription}
            isVisibleToHCP={document.isVisibleToHCP}
            htmlContent={htmlContent}
            alternativeTextFileName={alternativeTextFileName}
            onAlternativeTextFileNameClick={openAlternativeTextFile}
            isAdmin={isAdmin}
            secondaryFileName={secondaryOriginalFileName}
            onSecondaryFileClick={onSecondaryFileDownload}
            documentProcessingDetails={documentProcessingDetails}
            details={details}
            isAccessiblePilRequested={document?.isAccessiblePilRequested}
          />
        ),
      },
      {
        label: `Version history`,
        content: (
          <DocumentVersionHistory
            isAdmin={isAdmin}
            documentId={document?.documentId ?? 0}
            documentType={document?.type}
            showHistoryFiltering={showHistoryFiltering}
          />
        ),
      },
    ];

    if (displayHtmlViewTab)
      items.push({
        label: `View html`,
        content: (
          <HtmlDocument documentId={document?.documentId.toString()} documentType={contentType} version={version.toString()} />
        ),
      });

    return items;
  };

  const renderContent = () => {
    switch (contentType) {
      case DocumentType.Smpc:
      case DocumentType.Pil:
      case DocumentType.Rmm:
      case DocumentType.Dhcp:
      case DocumentType.UserManual:
      case DocumentType.SafetyAlert:
      case DocumentType.ProductInformation:
      case DocumentType.AudioVideo:
      case DocumentType.Audio:
      case DocumentType.Video:
        return <Tabs items={getTabs() }
                  selectedTab={selectedTab}
                  onTabSelected={(selectedTab) => setSelectedTab(selectedTab)}
                  rightSideNode={
                    canPrint ? (
                        <Button
                          type={ButtonTypes.PRIMARY}
                          text="Print"
                          height={35}
                          width={170}
                          color={theme.elements.defaultWhite}
                          onClick={onPrint}
                          tooltip='Click here to see the html view, and print or generate a PDF. Please note that the PDF output will depend on the tool within your browser and there may be some differences between its presentation and how the document appears on emc - e.g. some special characters and table width may be rendered differently when generating a PDF using different browsers.'
                        />
                    ) : null
                  } />;
      default:
        return <>Work in progress</>;
    }
  };

  const configuration = useAppSelector(selectConfiguration);
  const { showQrCodeForPil } = useFeatureFlags();
  const { companyConfiguration } = useFetchCompanyConfiguration(companyId);

  const { emcShortLink, emcUrlTitle } = getEmcShortLinkDetails(attributes, linkedProductId, contentType, configuration.emcShortLink, companyConfiguration);

  return (
    <>
      <Styled.ContentDetailsWrap>
        <SubmissionFlowHeader
          title={formatDocumentSubTitle(isAdmin, title, document, false)}
          onCloseClick={onCancel}
          onBackClick={onCancel}
          emcUrl={emcShortLink}
          showQrCode={showQrCodeForPil}
          emcUrlTitle={emcUrlTitle}
        />
        {!documentFetching && renderContent()}
      </Styled.ContentDetailsWrap>
      <SubmissionFlowFooter onCancel={onCancel} cancelText="Back" />
    </>
  );
};

export default ReadonlyDocumentDetailsContent;
